import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

export interface BudgetLineForecast {
  budget_line_id: string;
  category_name: string;
  budget_amount: number;
  realized_amount: number;
  forecast_amount: number;
  variance_amount: number;
  variance_percent: number;
  status: 'ON_TRACK' | 'WARNING' | 'ALERT';
  confidence_level: 'LOW' | 'MEDIUM' | 'HIGH';
  method: 'RUN_RATE' | 'PRORATA' | 'NO_DATA' | 'MANUAL';
  has_override: boolean;
  budget_line_year: number;
}

export interface ExerciseLanding {
  total_budget_income: number;
  total_budget_expense: number;
  total_budget_balance: number;
  total_realized_income: number;
  total_realized_expense: number;
  total_realized_balance: number;
  total_forecast_income: number;
  total_forecast_expense: number;
  total_forecast_balance: number;
  variance_income: number;
  variance_expense: number;
  variance_balance: number;
  exercise_start_date: string;
  exercise_end_date: string;
  months_elapsed: number;
  months_remaining: number;
}

export function useExerciseLanding() {
  const { effectiveUserId } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [exerciseLanding, setExerciseLanding] = useState<ExerciseLanding | null>(null);
  const [lineForecasts, setLineForecasts] = useState<BudgetLineForecast[]>([]);

  const loadExerciseLanding = useCallback(async (year?: number) => {
    if (!effectiveUserId) return null;

    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc('calculate_exercise_landing', {
        p_user_id: effectiveUserId,
        p_year: year || null
      });

      if (error) {
        console.error('Error loading exercise landing:', error);
        return null;
      }

      if (data && data.length > 0) {
        const landing = data[0] as ExerciseLanding;
        setExerciseLanding(landing);
        return landing;
      }

      return null;
    } finally {
      setIsLoading(false);
    }
  }, [effectiveUserId]);

  const loadBudgetLineForecast = useCallback(async (
    budgetLineId: string
  ): Promise<BudgetLineForecast | null> => {
    if (!effectiveUserId) return null;

    const { data, error } = await supabase.rpc('calculate_budget_line_forecast', {
      p_budget_line_id: budgetLineId,
      p_user_id: effectiveUserId
    });

    if (error) {
      console.error('Error loading budget line forecast:', error);
      return null;
    }

    if (data && data.length > 0) {
      return data[0] as BudgetLineForecast;
    }

    return null;
  }, [effectiveUserId]);

  const loadAllLineForecasts = useCallback(async (year: number) => {
    if (!effectiveUserId) return [];

    setIsLoading(true);
    try {
      const { data: budgetLines, error: blError } = await supabase
        .from('budget_lines')
        .select('id')
        .eq('user_id', effectiveUserId)
        .eq('year', year);

      if (blError || !budgetLines) {
        console.error('Error loading budget lines:', blError);
        return [];
      }

      const forecasts: BudgetLineForecast[] = [];

      for (const line of budgetLines) {
        const forecast = await loadBudgetLineForecast(line.id);
        if (forecast) {
          forecasts.push(forecast);
        }
      }

      forecasts.sort((a, b) => Math.abs(b.variance_amount) - Math.abs(a.variance_amount));

      setLineForecasts(forecasts);
      return forecasts;
    } finally {
      setIsLoading(false);
    }
  }, [effectiveUserId, loadBudgetLineForecast]);

  const saveOverride = useCallback(async (
    budgetLineId: string,
    year: number,
    overrideAmount: number,
    note?: string
  ) => {
    if (!effectiveUserId) return false;

    const { error } = await supabase
      .from('forecast_line_overrides')
      .upsert({
        user_id: effectiveUserId,
        budget_line_id: budgetLineId,
        year,
        override_amount: overrideAmount,
        note: note || null,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id,budget_line_id,year'
      });

    if (error) {
      console.error('Error saving override:', error);
      return false;
    }
    return true;
  }, [effectiveUserId]);

  const deleteOverride = useCallback(async (budgetLineId: string, year: number) => {
    if (!effectiveUserId) return false;

    const { error } = await supabase
      .from('forecast_line_overrides')
      .delete()
      .eq('user_id', effectiveUserId)
      .eq('budget_line_id', budgetLineId)
      .eq('year', year);

    if (error) {
      console.error('Error deleting override:', error);
      return false;
    }
    return true;
  }, [effectiveUserId]);

  const generateAnalysisText = useCallback((landing: ExerciseLanding): string => {
    if (!landing) return '';

    const parts: string[] = [];

    const varSign = landing.variance_balance >= 0 ? 'excédentaire' : 'déficitaire';
    const forecastBalance = landing.total_forecast_balance;

    parts.push(
      `À ce rythme, vous termineriez l'exercice avec un solde ${varSign} de ${Math.abs(forecastBalance).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €. `
    );

    parts.push(
      `Recettes prévisionnelles : ${landing.total_forecast_income.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € `
    );
    parts.push(
      `(écart de ${landing.variance_income.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'always' })} €). `
    );
    parts.push(
      `Dépenses prévisionnelles : ${landing.total_forecast_expense.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} € `
    );
    parts.push(
      `(écart de ${landing.variance_expense.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: 'always' })} €).`
    );

    return parts.join('');
  }, []);

  const getConfidenceLabel = useCallback((score: number): string => {
    if (score >= 0.7) return 'Élevée';
    if (score >= 0.4) return 'Moyenne';
    return 'Faible';
  }, []);

  const getStatusColor = useCallback((status: string): string => {
    switch (status) {
      case 'ON_TRACK':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'WARNING':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'ALERT':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  }, []);

  const getStatusLabel = useCallback((status: string): string => {
    switch (status) {
      case 'ON_TRACK':
        return 'Conforme';
      case 'WARNING':
        return 'Attention';
      case 'ALERT':
        return 'Alerte';
      default:
        return 'Inconnu';
    }
  }, []);

  const getMethodLabel = useCallback((method: string): string => {
    switch (method) {
      case 'RUN_RATE':
        return 'Run-rate';
      case 'PRORATA':
        return 'Prorata';
      case 'NO_DATA':
        return 'Aucune donnée';
      case 'MANUAL':
        return 'Manuel';
      default:
        return method;
    }
  }, []);

  return {
    isLoading,
    exerciseLanding,
    lineForecasts,
    loadExerciseLanding,
    loadBudgetLineForecast,
    loadAllLineForecasts,
    saveOverride,
    deleteOverride,
    generateAnalysisText,
    getConfidenceLabel,
    getStatusColor,
    getStatusLabel,
    getMethodLabel
  };
}
